import { diContainer, serviceNames } from '../../diContainer/diContainer';
import ShippingRepository from '../../repositories/ShippingRepository';
import TransactionRepository from '../../repositories/TransactionRepository';
import Address, { AddressId, IAddressAttributes } from '../../domain/entities/Address';
import Config from '../../domain/entities/Config';
import SessionRepository from '../../repositories/SessionRepository';
import BopisStoresRepository from '../../repositories/BopisStoresRepository';
import CourierStoresRepository from '../../repositories/CourierStoresRepository';
import { FIELD_TYPE } from '../../constants/Constants';

export default class LoadStoresService {
  private config: Config;

  private transactionRepository: TransactionRepository;

  private shippingRepository: ShippingRepository;

  private sessionRepository: SessionRepository;

  private bopisStoresRepository: BopisStoresRepository;

  private courierStoresRepository: CourierStoresRepository;

  constructor() {
    this.transactionRepository = diContainer.get(serviceNames.transactionRepository);
    this.shippingRepository = diContainer.get(serviceNames.shippingRepository);
    this.sessionRepository = diContainer.get(serviceNames.sessionRepository);
    this.config = diContainer.get(serviceNames.config);
    this.bopisStoresRepository = diContainer.get(serviceNames.bopisStoresRepository);
    this.courierStoresRepository = diContainer.get(serviceNames.courierStoresRepository);
  }

  private delayLoadStoresByAddress = (address: string | Address | IAddressAttributes): void => {
    setTimeout(() => {
      if (!this.courierStoresRepository.courierStoresCollection.stores) {
        this.courierStoresRepository.getStoresByAddress(address);
      }
    }, 5000);
  };

  public loadCourierStoresByLocation = async (location?: AddressId | string | Partial<IAddressAttributes>): Promise<void> => {
    let dropoffAddress: Address | string | IAddressAttributes;
    if (location) {
      // Check inventory when zip input is valid.
      const schema = this.shippingRepository.formStore.getAddressValidationSchema(
        this.config && this.config.shippingAddressInputSchema,
      );
      const zipPatternRegex = new RegExp(schema.properties[FIELD_TYPE.ZIPCODE].pattern as string);
      if (this.shippingRepository.getAddressById(location)) {
        // Check inventory for selected address.
        dropoffAddress = this.shippingRepository.getAddressById(location);
      } else if (typeof location === 'string') {
        if (zipPatternRegex.test(location)) {
          dropoffAddress = location;
        }
      } else if (location[FIELD_TYPE.FIRST_LINE_ADDRESS] && location[FIELD_TYPE.ZIPCODE]) {
        if (zipPatternRegex.test(location[FIELD_TYPE.ZIPCODE])) {
          dropoffAddress = location;
        }
      }
    } else {
      const { shippingAddress } = await this.transactionRepository.getTransaction();
      if (!shippingAddress.isNewAddress) {
        dropoffAddress = shippingAddress;
      } else {
        await this.courierStoresRepository.loadStoresByUserLocation();
      }
    }

    if (dropoffAddress) {
      this.courierStoresRepository.getCoordinatesFromAddress(dropoffAddress);
      // Back up inventory call by address, only if Google Maps API fails
      this.delayLoadStoresByAddress(dropoffAddress);
    }
    const { courierStoresCollection } = this.courierStoresRepository;
    courierStoresCollection.setIsAddressUndeliverable(false);
  };

  public loadBopisStoresByLocation = async (isClickAndCollect = false, storeType = 0): Promise<void> => {
    const userAddress = this.sessionRepository.userAddress;
    const localStorageZip = this.sessionRepository.userLocalStorageZip;

    if (userAddress?.lat && userAddress?.lng) {
      await this.bopisStoresRepository.getStoresByGeolocation({
        latitude: userAddress.lat,
        longitude: userAddress.lng,
      });
    } else if (localStorageZip) {
      await this.bopisStoresRepository.getCoordinatesFromAddress(localStorageZip);
    } else {
      await this.bopisStoresRepository.loadStoresByUserLocation(isClickAndCollect, storeType);
    }
  };
}
